import React from 'react';
import { ProfitLossPoint } from '../types/billing';
import { formatAmountShort } from '../utils/formatters';

interface ProfitLossLineChartProps {
  data: ProfitLossPoint[];
}

// Modern line chart with area fill
const ProfitLossLineChart: React.FC<ProfitLossLineChartProps> = ({ data }) => {
  const width = 900;
  const height = 400;
  const padding = { top: 20, right: 20, bottom: 60, left: 80 };

  const periods = data.map(d => d.period);
  const maxValue = Math.max(
    1,
    ...data.map(d => Math.max(d.billedAmount, d.receivedAmount))
  );

  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const xScale = (index: number) => (index / (periods.length - 1)) * innerWidth;
  const yScale = (v: number) => innerHeight - (v / maxValue) * innerHeight;

  // Create path for billed amounts
  const billedPath = data.length > 0 ? data.map((d, idx) => {
    const x = xScale(idx);
    const y = yScale(d.billedAmount);
    return `${idx === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ') : '';

  // Create path for received amounts
  const receivedPath = data.length > 0 ? data.map((d, idx) => {
    const x = xScale(idx);
    const y = yScale(d.receivedAmount);
    return `${idx === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ') : '';

  // Create area path for billed amounts
  const billedAreaPath = data.length > 0 ? `${billedPath} L ${xScale(data.length - 1)} ${innerHeight} L ${xScale(0)} ${innerHeight} Z` : '';

  // Create area path for received amounts
  const receivedAreaPath = data.length > 0 ? `${receivedPath} L ${xScale(data.length - 1)} ${innerHeight} L ${xScale(0)} ${innerHeight} Z` : '';

  // Modern color scheme
  const colors = {
    billed: '#3B82F6', // Blue-500
    received: '#10B981', // Emerald-500
    billedArea: '#3B82F620', // Blue-500 with 20% opacity
    receivedArea: '#10B98120', // Emerald-500 with 20% opacity
    grid: '#F3F4F6', // Gray-100
    text: '#374151', // Gray-700
    textLight: '#6B7280', // Gray-500
    background: '#FAFAFA' // Gray-50
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg p-6 overflow-x-auto border border-gray-200">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-800 mb-2">Revenue Trends</h4>
        <p className="text-sm text-gray-600">Monthly trends of billed vs received amounts</p>
      </div>
      
      <svg width={width} height={height} className="min-w-full">
        {/* Background */}
        <rect width={width} height={height} fill={colors.background} rx="8" />
        
        {/* Main chart area */}
        <g transform={`translate(${padding.left},${padding.top})`}>
          {/* Y axis grid lines */}
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((t, i) => {
            const y = innerHeight - t * innerHeight;
            const value = Math.round(maxValue * t);
            return (
              <g key={i}>
                <line 
                  x1={0} 
                  x2={innerWidth} 
                  y1={y} 
                  y2={y} 
                  stroke={colors.grid} 
                  strokeWidth={1}
                  strokeDasharray={i === 0 ? "none" : "2,2"}
                />
                <text 
                  x={-15} 
                  y={y} 
                  textAnchor="end" 
                  dominantBaseline="middle" 
                  fontSize={12} 
                  fill={colors.textLight}
                  fontWeight="500"
                >
                  {formatAmountShort(value)}
                </text>
              </g>
            );
          })}

          {/* X axis line */}
          <line 
            x1={0} 
            x2={innerWidth} 
            y1={innerHeight} 
            y2={innerHeight} 
            stroke={colors.textLight} 
            strokeWidth={2}
          />

          {/* Y axis line */}
          <line 
            x1={0} 
            x2={0} 
            y1={0} 
            y2={innerHeight} 
            stroke={colors.textLight} 
            strokeWidth={2}
          />

          {/* Area fills */}
          <path d={billedAreaPath} fill={colors.billedArea} />
          <path d={receivedAreaPath} fill={colors.receivedArea} />

          {/* Line paths */}
          <path 
            d={billedPath} 
            fill="none" 
            stroke={colors.billed} 
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path 
            d={receivedPath} 
            fill="none" 
            stroke={colors.received} 
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points and labels */}
          {data.map((d, idx) => {
            const x = xScale(idx);
            const billedY = yScale(d.billedAmount);
            const receivedY = yScale(d.receivedAmount);
            
            return (
              <g key={idx}>
                {/* Billed amount point */}
                <circle 
                  cx={x} 
                  cy={billedY} 
                  r={4} 
                  fill={colors.billed}
                  stroke="white"
                  strokeWidth={2}
                  className="hover:r-6 transition-all duration-200"
                >
                  <title>{`Billed: ${formatAmountShort(d.billedAmount)}`}</title>
                </circle>
                
                {/* Received amount point */}
                <circle 
                  cx={x} 
                  cy={receivedY} 
                  r={4} 
                  fill={colors.received}
                  stroke="white"
                  strokeWidth={2}
                  className="hover:r-6 transition-all duration-200"
                >
                  <title>{`Received: ${formatAmountShort(d.receivedAmount)}`}</title>
                </circle>
                
                {/* Period labels */}
                <text 
                  x={x} 
                  y={innerHeight + 20} 
                  textAnchor="middle" 
                  fontSize={11} 
                  fill={colors.text}
                  fontWeight="500"
                >
                  {d.period}
                </text>
              </g>
            );
          })}

          {/* Modern Legend */}
          <g transform={`translate(${innerWidth - 200}, 10)`}>
            <line x1={0} y1={7} x2={20} y2={7} stroke={colors.billed} strokeWidth={3} strokeLinecap="round" />
            <text x={25} y={11} fontSize={12} fill={colors.text} fontWeight="600">Billed Amount</text>
            <line x1={120} y1={7} x2={140} y2={7} stroke={colors.received} strokeWidth={3} strokeLinecap="round" />
            <text x={145} y={11} fontSize={12} fill={colors.text} fontWeight="600">Received Amount</text>
          </g>
        </g>
      </svg>
    </div>
  );
};

export default ProfitLossLineChart;
